import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { V2, timeAgo } from "./tokens";
import { Eyebrow, HairLine } from "./V2Shared";

const STATUS_COLOR: Record<string, string> = {
  proposed: V2.cyan2, in_progress: V2.violet, enacted: V2.green2, enforced: V2.green2,
};
const STAGES = ["proposed", "in_progress", "enacted"];
const stageIdx = (status: string) => (status === "enforced" ? 2 : Math.max(0, STAGES.indexOf(status)));

const jShort = (c: string) =>
  c.replace("United States", "US").replace("European Union", "EU").replace("United Kingdom", "UK");
const jAbbr = (c: string) => {
  const s = jShort(c);
  return (s.length <= 3 ? s : s.slice(0, 2)).toUpperCase();
};

const ROW_COLORS = [V2.violet, V2.cyan2, V2.green2];

/* Touch radar rings (Figma 58:2 / 60:2): three concentric observation rings
   with the real tracked jurisdictions as nodes. Decorative geometry only —
   labels come from real tracker data. */
const RadarRings = ({ jurisdictions }: { jurisdictions: string[] }) => {
  const angles = [-28, 178, 92];
  return (
    <div className="relative h-[216px] md:h-[292px] mt-[16px]" aria-hidden>
      <svg viewBox="0 0 360 292" className="w-full h-full">
        {[52, 88, 128].map((r) => (
          <circle key={r} cx="180" cy="146" r={r} fill="none" stroke={V2.border2} strokeWidth="1" />
        ))}
        {jurisdictions.slice(0, 3).map((c, i) => {
          const ang = (angles[i] * Math.PI) / 180;
          const x = 180 + 88 * Math.cos(ang);
          const y = 146 + 88 * Math.sin(ang);
          const col = ROW_COLORS[i % 3];
          const right = Math.cos(ang) >= 0;
          return (
            <g key={c}>
              <circle cx={x} cy={y} r="9" fill={col} fillOpacity="0.9" />
              <text x={x + (right ? 16 : -16)} y={y + 4} textAnchor={right ? "start" : "end"}
                className="nl-geist" fontSize="11" fontWeight={600} fill={V2.text}>
                {jAbbr(String(c))}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* Regulation Intelligence. Desktop (47:2 y4280, Gate-2.1 approved) unchanged
   at xl. Phase 3 touch card (58:2 / 60:2): Regulation Radar — adaptive stat,
   ring field, framework rows. Copy stays TRUTHFUL-ADAPTIVE: "changed this
   week" and "ACTIVE TRANSITIONS" render ONLY from observed status-history
   transitions (STATE B); baselines never render as movement. */
export const RegulationField = ({ data }: { data: any }) => {
  const { data: regsResp } = useQuery({ queryKey: ["v2-regulations"], queryFn: async () => (await api.get("/regulations", { params: { limit: 6 } })).data });
  const regs = (Array.isArray(regsResp) ? regsResp : regsResp?.items) || [];
  const pulse = data?.regulation_pulse;
  const devs = data?.regulation_developments || [];
  const linkedByReg: Record<string, { slug: string; title: string }> = {};
  devs.forEach((d: any) => {
    const id = d?.regulation?.id;
    if (id && !linkedByReg[id]) linkedByReg[id] = { slug: d.article.slug, title: d.article.title };
  });
  if (!regs.length) return null;
  const ringJurisdictions = [...new Set(regs.flatMap((r: any) => (r.countries || []).slice(0, 1)))].slice(0, 3) as string[];
  const observed = !!(pulse && pulse.observed_transitions_7d > 0);
  const noteFor = (r: any) => {
    const note = (r.timeline || [])[((r.timeline || []).length || 1) - 1];
    return note?.note || note?.status || (r.effective_date ? `effective ${r.effective_date}` : timeAgo(r.updated_at));
  };
  return (
    <section data-testid="v2-regulation-field" className="mt-[56px]">
      <Eyebrow color={V2.violet} size={11}>REGULATION INTELLIGENCE</Eyebrow>
      <h2 className="nl-sg text-[28px] leading-[34px] md:text-[34px] md:leading-[40px] xl:text-[38px] xl:leading-[44px] font-medium mt-[12px]" style={{ color: V2.text }}>
        <span className="md:hidden">Policy shifts that change the onchain economy.</span>
        <span className="hidden md:inline">Jurisdictions are moving at different speeds.</span>
      </h2>
      <p className="nl-geist text-[15px] leading-[23px] md:text-[16px] md:leading-[25px] mt-[10px] max-w-[900px]" style={{ color: V2.body }}>
        <span className="md:hidden">Status changes, implementation pressure and enforcement as a living field.</span>
        <span className="hidden md:inline">See regulation as an evolving field of status changes, implementation pressure and enforcement — not a static list.</span>
      </p>

      {/* ============ 390 native (206:287): compact framework rows ============ */}
      <div className="md:hidden mt-[18px] rounded-[16px] p-[16px]"
        style={{ background: "rgba(7,7,10,0.65)", border: `1px solid ${V2.border2}` }}
        data-testid="v2-regulation-mobile">
        {regs.slice(0, 4).map((r: any, i: number) => {
          const linked = linkedByReg[r.id];
          return (
            <div key={r.id || i} className={`py-[12px] ${i > 0 ? "border-t border-[#242432]" : "pt-[2px]"}`}
              data-testid={`v2-regulation-mobile-row-${i + 1}`}>
              <div className="flex items-baseline gap-[10px]">
                <span className="nl-geist text-[9px] leading-[13px] font-semibold tracking-[0.1em]" style={{ color: V2.metaMuted }}>
                  {(r.countries || []).map(jShort).join(" · ").toUpperCase() || "—"}
                </span>
                <span className="nl-geist text-[12px] leading-[16px] font-semibold uppercase" style={{ color: STATUS_COLOR[r.status] || V2.text }}>
                  {(r.agencies || []).slice(0, 2).join(" / ") || String(r.status || "").replace("_", " ")}
                </span>
              </div>
              <p className="nl-geist text-[12px] leading-[17px] mt-[5px] line-clamp-2" style={{ color: V2.body }}>
                {r.title}
              </p>
              {linked && (
                <Link to={`/article/${linked.slug}`} className="nl-geist block text-[11px] leading-[15px] mt-[4px] truncate"
                  style={{ color: V2.cyan2 }}>
                  {linked.title} →
                </Link>
              )}
            </div>
          );
        })}
        <Link to="/policy" data-testid="v2-regulation-mobile-cta"
          className="nl-geist inline-flex items-center min-h-[44px] text-[10px] leading-[13px] font-semibold tracking-[0.1em]"
          style={{ color: V2.cyan2 }}>
          OPEN POLICY INTELLIGENCE →
        </Link>
      </div>

      {/* ============ 768+ (master 200:560 / 205:364): radar + active transitions ============ */}
      <div className="hidden md:block mt-[20px] rounded-[18px] xl:rounded-[22px] p-[22px] xl:p-[28px]"
        style={{ background: "rgba(7,7,10,0.65)", border: `1px solid ${V2.border2}` }}
        data-testid="v2-regulation-radar-touch">
        <div className="grid md:grid-cols-[44fr_56fr] gap-[26px] xl:gap-[40px]">
          <div>
            <Eyebrow color={V2.violet} size={10}>REGULATION RADAR</Eyebrow>
            {pulse && (
              <div className="flex items-baseline gap-[14px] xl:gap-[18px] mt-[10px]" data-testid="v2-regulation-radar-stat">
                <span className="nl-sg text-[40px] leading-[44px] xl:text-[46px] xl:leading-[50px] font-medium shrink-0" style={{ color: V2.text }}>
                  {observed ? pulse.frameworks_changed_7d : pulse.frameworks}
                </span>
                <span className="nl-geist text-[10px] leading-[14px] xl:text-[11px] xl:leading-[15px] font-semibold tracking-[0.06em] uppercase max-w-[230px]"
                  style={{ color: V2.metaMuted }}>
                  {observed
                    ? "FRAMEWORKS CHANGED THIS WEEK"
                    : `FRAMEWORKS TRACKED ACROSS ${pulse.jurisdictions} JURISDICTIONS`}
                </span>
              </div>
            )}
            <RadarRings jurisdictions={ringJurisdictions} />
          </div>
          <div>
            <p className="nl-geist text-[10px] xl:text-[11px] leading-[14px] font-semibold uppercase tracking-[0.08em]" style={{ color: V2.cyan2 }}>
              {observed ? "ACTIVE TRANSITIONS" : "ACTIVE FRAMEWORKS"}
            </p>
            <div>
              {regs.slice(0, 3).map((r: any, i: number) => {
                const col = ROW_COLORS[i % 3];
                const idx = stageIdx(r.status);
                const linked = linkedByReg[r.id];
                return (
                  <div key={r.id || i} data-testid={`v2-regulation-touch-row-${i + 1}`}
                    className={`pt-[16px] pb-[14px] ${i > 0 ? "border-t border-[#242432]" : ""}`}>
                    <p className="nl-geist text-[10px] xl:text-[11px] leading-[13px] font-semibold tracking-[0.08em]" style={{ color: col }}>
                      {(r.countries || []).map(jShort).join(" · ").toUpperCase() || "—"}
                    </p>
                    <div className="flex items-center gap-[12px] mt-[8px] min-w-0">
                      <span className="nl-geist text-[16px] xl:text-[18px] leading-[22px] font-semibold uppercase shrink-0" style={{ color: V2.text }}>
                        {String(r.status || "").replace("_", " ")}
                      </span>
                      <span className="flex items-center gap-[6px] shrink-0" aria-hidden>
                        {STAGES.map((s, si) => (
                          <span key={s} className="h-[8px] w-[8px] rounded-full"
                            style={{ background: si <= idx ? col : V2.border2 }} />
                        ))}
                      </span>
                      <span aria-hidden className="flex-1 h-px" style={{ background: V2.border2 }} />
                      <span className="nl-geist w-[170px] xl:w-[210px] text-[12px] leading-[17px] shrink-0" style={{ color: V2.body }}>
                        {noteFor(r)}
                      </span>
                    </div>
                    <p className="nl-geist text-[10px] leading-[14px] mt-[4px] truncate" style={{ color: V2.metaMuted }}>
                      {r.title}
                    </p>
                    {linked && (
                      <Link to={`/article/${linked.slug}`} data-testid={`v2-regulation-touch-linked-${i + 1}`}
                        className="group/link flex items-center gap-[8px] mt-[6px] min-w-0">
                        <span className="nl-geist text-[9px] leading-[13px] font-semibold tracking-[0.1em] shrink-0" style={{ color: V2.cyan2 }}>
                          LINKED COVERAGE
                        </span>
                        <span className="nl-geist text-[12px] leading-[17px] truncate group-hover/link:text-white transition-colors duration-200" style={{ color: V2.body }}>
                          {linked.title} →
                        </span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <HairLine className="mt-[14px]" />
        {pulse && (
          <div className="flex flex-wrap items-center gap-x-[18px] gap-y-[8px] mt-[16px]" data-testid="v2-regulation-tracked-now">
            <span className="nl-geist text-[10px] leading-[14px] font-semibold tracking-[0.1em]" style={{ color: V2.metaMuted }}>
              TRACKED NOW
            </span>
            <span className="nl-geist text-[13px] xl:text-[14px] leading-[19px]" style={{ color: V2.body }}>
              {pulse.frameworks} frameworks · {pulse.jurisdictions} jurisdictions
              {pulse.observed_transitions_7d > 0 ? ` · ${pulse.observed_transitions_7d} active status transition${pulse.observed_transitions_7d === 1 ? "" : "s"}` : ""}
            </span>
            <Link to="/policy" data-testid="v2-regulation-touch-cta"
              className="nl-geist ml-auto inline-flex items-center min-h-[44px] text-[10px] xl:text-[11px] leading-[13px] font-semibold tracking-[0.1em] hover:translate-x-[2px] transition-transform duration-200"
              style={{ color: V2.cyan2 }}>
              OPEN POLICY INTELLIGENCE →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
